import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { DomainRow } from '../components/demoCenter/DomainRow';
import { DomainDetailDialog } from '../components/demoCenter/DomainDetailDialog';
import { GovernmentFeature } from '../components/demoCenter/GovernmentFeature';
import { DemoFoundation } from '../components/demoCenter/DemoFoundation';
import { demoDomainGroups, demoDomains, demoMilestoneNote, sharedFoundationCopy, type DemoDomain } from '../data/demoCenter';
import { fadeUp } from '../utils/motion';
export function NoetivraDemoCenterPage() {
  const [openDomain, setOpenDomain] = useState<DemoDomain | null>(null);
  const [highlightedId, setHighlightedId] = useState<DemoDomain['id'] | null>(null);

  // A domain anchor (e.g. #talent) highlights its row; planned domains also open their detail.
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    const domain = demoDomains.find(item => item.id === hash);
    if (!domain) return;
    setHighlightedId(domain.id);
    if (domain.status === 'planned') {
      const timer = window.setTimeout(() => setOpenDomain(domain), 250);
      return () => window.clearTimeout(timer);
    }
  }, []);
  const closeDialog = useCallback(() => setOpenDomain(null), []);
  const explore = (selected: DemoDomain) => {
    setHighlightedId(selected.id);
    setOpenDomain(selected);
  };
  return <>
      <PageHero eyebrow="Noetivra Demo Center" title="Seven domains. One connected foundation." lead="Pick a domain, then choose a workspace. Every demo runs on fictional records." body={[sharedFoundationCopy]} backLink={{
      href: '/noetivra',
      label: 'Noetivra'
    }} data-id="element-3094" data-mp="2s4bom9yu75k4">
        <div className="mt-8" data-id="element-3095" data-mp="2ebn2v0n4t803">
          <a href="#domains" className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-3096" data-mp="2eau2qiiohgqx">
            Choose a domain
            <ArrowDown size={18} aria-hidden="true" data-id="element-3097" data-mp="i88ok6a9x0ol" />
          </a>
        </div>
      </PageHero>

      <section id="domains" aria-labelledby="domains-title" className="scroll-mt-20 bg-white border-b border-line py-16 lg:py-24" data-id="element-3098" data-mp="n11sirxx44zm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3099" data-mp="r0sn76qkf59f">
          <motion.div {...fadeUp} className="max-w-3xl mb-10" data-id="element-3100" data-mp="3qhqyabpofao8">
            <h2 id="domains-title" className="font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight" data-id="element-3101" data-mp="3vfsemovpwvw0">
              Choose a domain
            </h2>
            <p className="mt-4 text-ink-muted leading-relaxed" data-id="element-3102" data-mp="3echsbtrog3h">
              All seven are working demos and open in a new tab. Clinical is deliberately scoped to documentation and review.
            </p>
          </motion.div>

          <div className="space-y-14" data-id="element-3103" data-mp="1jpq6nd6ij83r">
            {demoDomainGroups.map(group => {
            const domains = group.ids.map(id => demoDomains.find(domain => domain.id === id)).filter((domain): domain is DemoDomain => Boolean(domain));
            const groupId = `group-${group.ids[0]}`;
            return <section key={group.title} aria-labelledby={groupId} data-id="element-3104" data-mp="1k2ab5tswb2le">
                  <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 border-b border-ink-dark/80 pb-3" data-id="element-3105" data-mp="1g2074monym96">
                    <h3 id={groupId} className="font-heading font-semibold text-ink-dark" data-id="element-3106" data-mp="1b1c6d9amur1r">
                      {group.title}
                    </h3>
                    <p className="text-sm text-ink-muted" data-id="element-3107" data-mp="116kac9wp8c93">{group.description}</p>
                  </div>
                  <ul className="divide-y divide-line" data-id="element-3108" data-mp="x58pehorsf78">
                    {domains.map(domain => <li key={domain.id} data-id="element-3109" data-mp="2vj09yruzos4z">
                        <DomainRow domain={domain} highlighted={highlightedId === domain.id} onExplore={explore} data-id="element-3110" data-mp="2rhyjp0j1vqf5" />
                      </li>)}
                  </ul>
                </section>;
          })}
          </div>

          <p className="mt-12 max-w-3xl border-l-2 border-lavender pl-4 text-sm leading-relaxed text-ink-muted" data-id="element-3111" data-mp="33jpskwzntbyr">{demoMilestoneNote}</p>
        </div>
      </section>

      <GovernmentFeature data-id="element-3112" data-mp="2tlioheqgyoe6" />

      <DemoFoundation data-id="element-3113" data-mp="2tkqs41p1smmu" />

      <DomainDetailDialog domain={openDomain} onClose={closeDialog} data-id="element-3114" data-mp="2to4x7hp3j9xz" />
    </>;
}